import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Search as SearchIcon } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/lib/permissions';
import HeaderSearchPanel from './HeaderSearchPanel';
import { allowedSources, placeholderFor } from './headerSearchSources';
import useHeaderSearch, { MIN_CHARS } from './useHeaderSearch';
import useRecentSearches from './useRecentSearches';
import useSearchShortcut from './useSearchShortcut';

const KBD = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '') ? '⌘K' : 'Ctrl K';

// Header search: live matches (jobs, leads, permits — whatever this person can see), recent
// searches, Ctrl+K / "/" to jump in. Enter opens the full list for the first group with matches.
export default function HeaderSearch() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { can } = usePermissions();
  const sources = useMemo(() => allowedSources({ can, user, flags: user?.integration_flags }), [can, user]);
  const recent = useRecentSearches(user?.id);
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [pending, setPending] = useState(false);
  const wrap = useRef(null);
  const input = useRef(null);
  useSearchShortcut(input);
  const res = useHeaderSearch(text, sources);
  const q = text.trim();
  const ready = res.status !== 'loading' && res.q === q;

  const options = useMemo(() => {
    if (!q) return recent.items.map((term) => ({ kind: 'recent', term }));
    if (!ready) return [];
    return res.groups.flatMap((g) => (g.items.length ? [...g.items.map((it) => ({ kind: 'item', g, it })), { kind: 'all', g }] : []));
  }, [q, ready, res, recent.items]);
  const shown = open && (q.length >= MIN_CHARS || (!q && recent.items.length > 0));

  useEffect(() => { setActive(-1); }, [options]);
  useEffect(() => { setPending(false); }, [q]);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const go = (to) => { recent.add(q); setText(''); setOpen(false); setActive(-1); navigate(to); };
  const pick = (o) => {
    if (o.kind === 'recent') { setText(o.term); setOpen(true); input.current?.focus(); return; }
    go(o.kind === 'item' ? o.g.source.open(o.it) : o.g.all.to);
  };
  // Enter with nothing highlighted: the first group with matches (Jobs, then Leads, then Permits).
  const fallback = () => {
    const g = res.groups.find((x) => x.total > 0);
    if (g) go(g.all.to);
    else if (sources.some((s) => s.key === 'jobs')) go(`/jobs?search=${encodeURIComponent(q)}`);
  };
  useEffect(() => { if (pending && ready) { setPending(false); fallback(); } }); // eslint-disable-line react-hooks/exhaustive-deps

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); setActive(-1); return; }
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && options.length) {
      e.preventDefault();
      setOpen(true);
      const n = options.length;
      setActive((a) => (e.key === 'ArrowDown' ? (a >= n - 1 ? 0 : a + 1) : (a <= 0 ? n - 1 : a - 1)));
    }
  };
  const onSubmit = (e) => {
    e.preventDefault();
    if (shown && options[active]) pick(options[active]);
    else if (q.length >= MIN_CHARS && !ready) setPending(true);
    else if (q) fallback();
  };

  if (!sources.length) return <div className="flex-1 max-w-sm" />;
  const busy = shown && q.length >= MIN_CHARS && !ready;
  return (
    <form ref={wrap} onSubmit={onSubmit} role="search" data-testid="topbar-search-form"
      className="relative flex items-center gap-2 px-3 py-1.5 rounded-md flex-1 max-w-sm" style={{ background: 'var(--strata-canvas-soft)' }}>
      {busy
        ? <Loader2 className="w-3.5 h-3.5 flex-shrink-0 animate-spin" style={{ color: 'var(--strata-text-muted)' }} data-testid="topbar-search-spinner" />
        : <SearchIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--strata-text-muted)' }} />}
      <input ref={input} data-testid="topbar-search" type="text" value={text} placeholder={placeholderFor(sources)} autoComplete="off"
        role="combobox" aria-expanded={shown} aria-controls="topbar-search-list" aria-autocomplete="list"
        aria-activedescendant={shown && active >= 0 ? `topbar-search-opt-${active}` : undefined}
        onChange={(e) => { setText(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onClick={() => setOpen(true)} onKeyDown={onKeyDown}
        className="bg-transparent border-0 outline-none text-xs flex-1"
        style={{ color: 'var(--strata-text-primary)', fontFamily: 'var(--strata-font-body)' }} />
      <kbd className="hidden xl:inline text-[9px] px-1 py-0.5 rounded border" style={{ color: 'var(--strata-text-muted)', borderColor: 'var(--strata-border)' }}
        data-testid="topbar-search-kbd" title={`${KBD} or / to search`}>{KBD}</kbd>
      {shown && (
        <div data-testid="topbar-search-dropdown"
          className="absolute left-0 top-full mt-1.5 w-[420px] max-w-[90vw] bg-white border rounded-md shadow-lg overflow-hidden z-30 animate-in fade-in-0 slide-in-from-top-1 duration-150"
          style={{ borderColor: 'var(--strata-border)', fontFamily: 'var(--strata-font-body)' }}>
          <HeaderSearchPanel q={q} res={res} ready={ready} options={options} sources={sources} active={active} setActive={setActive}
            onPick={pick} onClearRecent={() => { recent.clear(); input.current?.focus(); }} />
        </div>
      )}
    </form>
  );
}
